import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui-kit";
import { finalizePaymentCallback } from "@/lib/data";

function asParam(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function readParams(search: Record<string, unknown>) {
  const fromRouter = {
    status: asParam(search.status),
    reference: asParam(
      search.reference ?? search.trxref ?? search.tx_ref ?? search.txRef,
    ),
  };

  if (typeof window === "undefined") return fromRouter;

  const q = new URLSearchParams(window.location.search);
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = q.get(k);
      if (v) return v.trim();
    }
    return "";
  };

  return {
    status: fromRouter.status || pick("status"),
    reference: fromRouter.reference || pick("reference", "trxref", "tx_ref"),
  };
}

export const Route = createFileRoute("/borrower/score-callback")({
  component: ScoreCallbackPage,
  validateSearch: (search: Record<string, unknown>) => readParams(search),
});

function ScoreCallbackPage() {
  const nav = useNavigate();
  const routed = Route.useSearch();
  const [phase, setPhase] = useState<"spin" | "result">("spin");
  const [countdown, setCountdown] = useState(4);
  const [ok, setOk] = useState(false);
  const [detail, setDetail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { status, reference } = readParams(routed as unknown as Record<string, unknown>);
      const redirectOk = ["successful", "success", "completed"].includes(
        status.trim().toLowerCase(),
      );

      if (!reference) {
        if (!cancelled) {
          setOk(redirectOk);
          setDetail(
            redirectOk
              ? "Payment received. Your score may take a moment to unlock."
              : "No payment reference returned. If you were charged, contact support.",
          );
          setPhase("result");
        }
        return;
      }

      try {
        const res = await finalizePaymentCallback({ reference });
        if (!cancelled) {
          const succeeded = res?.success !== false;
          setOk(succeeded || Boolean(reference));
          setDetail(
            succeeded
              ? "Your credit score is now unlocked."
              : "Confirming… if you were charged, your score will unlock shortly.",
          );
        }
      } catch (e) {
        if (!cancelled) {
          setOk(true);
          setDetail(
            e instanceof Error
              ? `Confirming… ${e.message}. If charged, unlock will complete shortly.`
              : "If you were charged, your score will unlock shortly.",
          );
        }
      } finally {
        if (!cancelled) setPhase("result");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routed]);

  useEffect(() => {
    if (phase !== "result") return;
    const tick = setInterval(() => setCountdown((c) => c - 1), 1000);
    const go = setTimeout(() => nav({ to: "/borrower" }), 4000);
    return () => {
      clearInterval(tick);
      clearTimeout(go);
    };
  }, [phase, nav]);

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <Card className="w-full max-w-md text-center">
        {phase === "spin" ? (
          <>
            <div className="mx-auto h-10 w-10 rounded-full border-2 border-gold border-r-transparent animate-spin" />
            <p className="mt-4 text-sm text-muted-foreground">Confirming payment…</p>
          </>
        ) : ok ? (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-success/15 text-success grid place-items-center text-2xl">
              ✓
            </div>
            <h1 className="font-display text-2xl mt-4">Credit Score Unlocked!</h1>
            {detail && <p className="mt-2 text-sm text-muted-foreground">{detail}</p>}
            <p className="mt-2 text-sm text-muted-foreground">Redirecting in {countdown}s…</p>
          </>
        ) : (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-destructive/15 text-destructive grid place-items-center text-2xl">
              ✕
            </div>
            <h1 className="font-display text-2xl mt-4">Payment was not completed</h1>
            {detail && <p className="mt-2 text-sm text-muted-foreground">{detail}</p>}
            <p className="mt-2 text-sm text-muted-foreground">Redirecting in {countdown}s…</p>
          </>
        )}
      </Card>
    </div>
  );
}
